/**
 * OAuth Callback Page
 *
 * Handles the redirect from Google OAuth via Cognito.
 * Processes the auth code and redirects to dashboard.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Hub } from 'aws-amplify/utils';
import { useAuthStore } from '../stores/authStore';

export default function AuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { initialize } = useAuthStore();
  const [error, setError] = useState(null);

  useEffect(() => {
    // Check for error in URL params
    const errorParam = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');

    if (errorParam) {
      setError(errorDescription || errorParam);
      setTimeout(() => {
        navigate('/login?error=oauth_failed');
      }, 2000);
      return;
    }

    // Listen for Amplify auth events
    const unsubscribe = Hub.listen('auth', async ({ payload }) => {
      switch (payload.event) {
        case 'signInWithRedirect':
          // OAuth callback successful
          try {
            await initialize();
            navigate('/dashboard');
          } catch {
            setError('Failed to complete sign-in');
            setTimeout(() => navigate('/login'), 2000);
          }
          break;

        case 'signInWithRedirect_failure':
          setError(payload.data?.message || 'OAuth sign-in failed');
          setTimeout(() => navigate('/login?error=oauth_failed'), 2000);
          break;

        case 'tokenRefresh':
          break;

        case 'tokenRefresh_failure':
          navigate('/login');
          break;

        default:
          break;
      }
    });

    // Check if we're already authenticated (in case Hub event was missed)
    const checkAuth = async () => {
      try {
        const { getCurrentUser } = await import('aws-amplify/auth');
        const user = await getCurrentUser();
        if (user) {
          await initialize();
          navigate('/dashboard');
        }
      } catch {
        // Not authenticated yet, wait for Hub event
      }
    };

    // Small delay to allow Amplify to process the callback
    const timer = setTimeout(checkAuth, 1000);

    return () => {
      unsubscribe();
      clearTimeout(timer);
    };
  }, [navigate, initialize, searchParams]);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
        <div className="bg-white p-8 rounded-lg shadow-md text-center">
          <svg
            className="mx-auto h-12 w-12 text-red-500 mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Erreur de connexion
          </h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <p className="text-sm text-gray-500">
            Redirection vers la page de connexion...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
      <div className="bg-white p-8 rounded-lg shadow-md text-center">
        {/* Loading spinner */}
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Connexion en cours...
        </h2>
        <p className="text-gray-600">
          Veuillez patienter pendant que nous finalisons votre connexion.
        </p>
      </div>
    </div>
  );
}
